import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { addDays } from 'date-fns';
import getDate from 'date-fns/getDate';
import parseISO from 'date-fns/parseISO';
import { observer } from 'mobx-react';
import { Grid } from '@material-ui/core';
import {
  paymentMethod,
  paymentType,
  originalGSNICStates,
  soldToEffectiveDaysIntervalByState,
  policyType as PolicyTypes
} from '@ourbranch/lookups';

import { useStore } from 'core/store';
import Section from 'core/components/section';
import { Card } from 'core/components/card';
import { Label } from 'core/components/label';
import { FormField } from 'core/components/form';
import { MegaDownPayCard } from './mega-down-pay-card';
import withDatePicker from 'core/components/with-date-picker';
import { canOfferMegaDownPay, isMegaDownPayRequired } from '../../helpers/mega-down-pay-helpers';
import { FutureEffectiveDateWarning } from './future-effective-date-warning';
import useStyles from './settings.styles';

const RentersSettings = observer(function RentersSettings({
  paymentMethodOptions,
  billingDaysOptions,
  paymentTypeOptions
}) {
  const { offer } = useStore();
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext();
  const { isBix } = offer;
  const state = offer?.state;
  const daysOut = soldToEffectiveDaysIntervalByState(state, PolicyTypes.Renters);
  const showCreditCardNote =
    values.global.rentersPaymentMethod === paymentMethod.ACH && !isBix && originalGSNICStates.includes(state);

  const offerMegaDownPay = useMemo(
    () =>
      canOfferMegaDownPay(
        values.global.rentersEffectiveDate,
        values.global.rentersBillingDayOfMonth,
        values.global.rentersPaymentType
      ),
    [values.global.rentersEffectiveDate, values.global.rentersBillingDayOfMonth, values.global.rentersPaymentType]
  );

  const megaDownPayRequired = useMemo(
    () =>
      isMegaDownPayRequired(
        values.global.rentersEffectiveDate,
        values.global.rentersBillingDayOfMonth,
        values.global.rentersPaymentType
      ),
    [values.global.rentersEffectiveDate, values.global.rentersBillingDayOfMonth, values.global.rentersPaymentType]
  );

  // if renters billing day of month is null, default select the effective day of the policy
  // or the 28th if the effectiveDay is past the allowed options of days 1 - 28
  useEffect(() => {
    const rentersEffectiveDay = getDate(parseISO(values.global.rentersEffectiveDate));
    if (!values.global.rentersBillingDayOfMonth && values.global.rentersBillingDayOfMonth !== rentersEffectiveDay) {
      if (rentersEffectiveDay > 28) {
        setFieldValue('global.rentersBillingDayOfMonth', 28);
      } else {
        setFieldValue('global.rentersBillingDayOfMonth', rentersEffectiveDay);
      }
    }
  }, [values.global.rentersBillingDayOfMonth, values.global.rentersEffectiveDate, setFieldValue]);

  return (
    <Section title="Renters Policy Settings" type="SubSection">
      <Card className={classes.container} type="secondary" id="rentersSettings">
        <Label type="formSubTitle">Policy Term</Label>
        <Grid container alignItems="center">
          <FormField
            type="date"
            name="global.rentersEffectiveDate"
            label="Renters policy effective date"
            xs={4}
            mode="dark"
            disableFuture={false}
            minDate={addDays(new Date(), daysOut)}
            maxDate={addDays(new Date(), 60)}
            fast={false}
          />
          <Grid item xs={12}>
            <FutureEffectiveDateWarning daysOut={daysOut} />
          </Grid>
        </Grid>
        <Grid container className={classes.billingDetails} alignItems="center" xs={12} spacing={2}>
          <Grid item xs={2}>
            <Label type="formSubTitle" className={classes.subtitle}>
              Billing Details
            </Label>
          </Grid>
          {showCreditCardNote && (
            <Grid item xs={6}>
              <Label type="noteSmall">Note: There is no additional fee for paying with credit card.</Label>
            </Grid>
          )}
        </Grid>
        <Grid container>
          <Grid container item xs={12} spacing={4}>
            <FormField
              label="Renters payment method"
              xs={4}
              type="select"
              name="global.rentersPaymentMethod"
              options={paymentMethodOptions}
              mode="dark"
            />
            <FormField
              label="Renters payment frequency"
              xs={4}
              type="select"
              name="global.rentersPaymentType"
              options={paymentTypeOptions}
              mode="dark"
              fast={false}
            />
            {values.global?.rentersPaymentType === paymentType.Monthly && (
              <FormField
                type="select"
                name="global.rentersBillingDayOfMonth"
                label="Renters Billing Day"
                mode="dark"
                options={billingDaysOptions}
                xs={4}
                fast={false}
              />
            )}
          </Grid>
        </Grid>
        {(offerMegaDownPay || megaDownPayRequired) && (
          <MegaDownPayCard megaDownPayRequired={megaDownPayRequired} formikValue="global.rentersMegaDownPay" />
        )}
      </Card>
    </Section>
  );
});

RentersSettings.propTypes = {
  paymentMethodOptions: PropTypes.array.isRequired,
  billingDaysOptions: PropTypes.array.isRequired,
  paymentTypeOptions: PropTypes.array.isRequired
};

export default withDatePicker(RentersSettings);
